import { ReactNode, useState } from 'react'
import { cn } from '@/lib/utils'
import { useSocialItemImage } from './useSocialItemImage'

interface SocialBrandMarkProps {
  /** The brand's stored picture (`SocialBrand.logoPath`); null or absent keeps the swatch. */
  logoPath?: string | null
  /** The brand's existing color mark, shown while there is no picture (also while loading or on error). */
  swatch: ReactNode
  /** `chip` 16 px (chips, tags, filter rows), `row` 20 px (share of voice rows). */
  size?: 'chip' | 'row'
  /** The picture's test id gets `-img`. Defaults to `social-brand-mark`. */
  testId?: string
}

const SOCIAL_BRAND_MARK_SIZE_CLASS: Record<'chip' | 'row', string> = {
  chip: 'h-4 w-4',
  row: 'h-5 w-5',
}

/**
 * A brand's mark next to its name: its stored profile picture when there is
 * one, else its color swatch. Decorative: the name beside it is the label.
 */
export function SocialBrandMark(props: SocialBrandMarkProps) {
  const { logoPath = null, swatch, size = 'chip', testId = 'social-brand-mark' } = props

  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const { src } = useSocialItemImage(logoPath)

  const onImageError = () => {
    setFailedSrc(src)
  }

  if (!src || src === failedSrc) {
    return <>{swatch}</>
  }
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className={cn('shrink-0 rounded-full border border-border object-cover', SOCIAL_BRAND_MARK_SIZE_CLASS[size])}
      onError={onImageError}
      data-testid={`${testId}-img`}
    />
  )
}
